import { useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { Calendar, CalendarDays } from "lucide-react";

// Local-timezone date stringifier — returns YYYY-MM-DD based on the user's
// local calendar (NOT UTC). Using toISOString() here would silently shift
// dates by ±1 day in non-UTC timezones (e.g. Asia/Riyadh +3 evenings).
const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Quick date preset chips + single-day picker.
 * Props:
 *   from, to              — current values (YYYY-MM-DD)
 *   onChange(from, to)    — fired when user picks a preset or a single day
 *   onClear?              — optional clear handler
 */
export default function DateFilterChips({ from, to, onChange, onClear }) {
  const { t } = useLang();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  function preset(key) {
    const t1 = new Date(today);
    const t2 = new Date(today);
    switch (key) {
      case "today":
        break;
      case "yesterday":
        t1.setDate(today.getDate() - 1);
        t2.setDate(today.getDate() - 1);
        break;
      case "last7":
        t1.setDate(today.getDate() - 6);
        break;
      case "last30":
        t1.setDate(today.getDate() - 29);
        break;
      case "week": {
        // Monday-start week
        const dow = today.getDay() === 0 ? 6 : today.getDay() - 1;
        t1.setDate(today.getDate() - dow);
        break;
      }
      case "month":
        t1.setDate(1);
        break;
      case "year":
        t1.setMonth(0, 1);
        break;
      default: return;
    }
    onChange(iso(t1), iso(t2));
  }

  // Detect current preset for visual highlight
  const todayIso = iso(today);
  const yIso = (() => { const d = new Date(today); d.setDate(today.getDate() - 1); return iso(d); })();
  const last7Iso = (() => { const d = new Date(today); d.setDate(today.getDate() - 6); return iso(d); })();
  const last30Iso = (() => { const d = new Date(today); d.setDate(today.getDate() - 29); return iso(d); })();
  const weekStartIso = (() => { const d = new Date(today); const dow = today.getDay() === 0 ? 6 : today.getDay() - 1; d.setDate(today.getDate() - dow); return iso(d); })();
  const monthStartIso = iso(new Date(today.getFullYear(), today.getMonth(), 1));
  const yearStartIso = iso(new Date(today.getFullYear(), 0, 1));

  // Detect which preset matches the current (from,to). Single-day chips win
  // over multi-day chips when the ranges collide (e.g. on Monday "today"
  // range == "this week" range; on the 1st of the month "today" == "this month").
  function detectActiveKey() {
    if (from === todayIso && to === todayIso) return "today";
    if (from === yIso && to === yIso) return "yesterday";
    if (from === last7Iso && to === todayIso) return "last7";
    if (from === last30Iso && to === todayIso) return "last30";
    if (from === weekStartIso && to === todayIso) return "week";
    if (from === monthStartIso && to === todayIso) return "month";
    if (from === yearStartIso && to === todayIso) return "year";
    return null;
  }
  const activeKey = detectActiveKey();

  const chips = [
    { key: "today",     label: t("date.today") },
    { key: "yesterday", label: t("date.yesterday") },
    { key: "week",      label: t("date.thisWeek") },
    { key: "last7",     label: t("date.last7") },
    { key: "last30",    label: t("date.last30") },
    { key: "month",     label: t("date.thisMonth") },
    { key: "year",      label: t("date.thisYear") },
  ];

  function onSingleDayPick(e) {
    const v = e.target.value;
    if (!v) return;
    onChange(v, v);
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="date-filter-chips">
      {chips.map((c) => (
        <button
          key={c.key}
          data-testid={`chip-${c.key}`}
          onClick={() => preset(c.key)}
          className={`text-xs px-3 py-1.5 rounded-full border transition ${
            c.active
              ? "bg-gold text-charcoal-900 border-gold shadow-[0_4px_14px_-4px_rgba(212,175,55,0.5)]"
              : "border-gold/30 text-foreground/70 hover:border-gold hover:text-gold"
          }`}
        >
          {c.label}
        </button>
      ))}
      <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-leaf/30 text-leaf hover:border-leaf cursor-pointer transition relative">
        <Calendar className="h-3.5 w-3.5" />
        <span>{t("date.pickDay")}</span>
        <input
          type="date"
          data-testid="chip-pick-day"
          onChange={onSingleDayPick}
          max={iso(today)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>
      {(from || to) && onClear && (
        <button
          onClick={onClear}
          data-testid="chip-clear"
          className="text-xs px-3 py-1.5 rounded-full border border-foreground/20 text-foreground/50 hover:text-foreground hover:border-foreground/40 transition inline-flex items-center gap-1.5"
        >
          <CalendarDays className="h-3.5 w-3.5" />
          {t("date.all")}
        </button>
      )}
    </div>
  );
}
